'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useFocusRing } from 'react-aria';
import { TONE } from '../system/tone';
import { interactiveBg, useInteractive } from '../system/interactive';
import { MOTION, focusRing } from '../system/motion';
const CARD_PAD_VAR = {
    flush: 0,
    compact: 'var(--space-lg)',
    default: 'var(--space-xl)',
    spacious: 'var(--space-2xl)',
};
function resolvePad(pad) {
    if (pad in CARD_PAD_VAR)
        return CARD_PAD_VAR[pad];
    return `var(--space-${pad})`;
}
export function Card(props) {
    return props.onClick ? _jsx(ClickableCard, { ...props }) : _jsx(StaticCard, { ...props });
}
function baseStyle({ p, tone, accentBorder, bordered, interactive, hover, active, showFocusRing, direction, clip, flush, extra, }) {
    // Cards always draw a border by default — including tone-tinted ones,
    // which previously suppressed the border and could fade into the page
    // background. Pass `bordered={false}` to deliberately opt out (rare;
    // appropriate inside another framing element). `accentBorder` overrides
    // the color but the border is always present.
    const showBorder = bordered;
    const background = tone
        ? TONE[tone].bg
        : interactive
            ? interactiveBg({ hover, active, focus: false, focusForced: false, disabled: false }, 'var(--card)')
            : 'var(--card)';
    const borderColor = accentBorder ? TONE[accentBorder].bg : 'var(--border)';
    const shouldClip = clip ?? (direction === 'column' || flush);
    return {
        background,
        color: tone ? TONE[tone].ink : 'var(--foreground)',
        border: showBorder ? `1px solid ${borderColor}` : 'none',
        borderRadius: 'var(--radius-lg)',
        padding: p,
        // Interactive cards show the pointer. Non-interactive cards leave the
        // cursor unset so the browser's natural per-element cursor applies — the
        // text I-beam over copy, the default arrow over empty card chrome. Forcing
        // `default` here previously suppressed the I-beam over selectable text.
        cursor: interactive ? 'pointer' : undefined,
        transition: interactive ? MOTION.transition : 'none',
        boxShadow: 'none',
        ...(direction ? { display: 'flex', flexDirection: direction } : {}),
        ...(shouldClip ? { overflow: 'hidden' } : {}),
        ...focusRing(showFocusRing),
        ...extra,
    };
}
function StaticCard({ pad = 'default', tone, accentBorder, bordered = true, interactive: interactiveProp, direction, clip, children, style, className, id, }) {
    const p = resolvePad(pad);
    const flush = pad === 'flush';
    const interactive = !!interactiveProp;
    const { state, bind } = useInteractive({});
    return (_jsx("div", { id: id, className: className, style: baseStyle({
            p, tone, accentBorder, bordered, interactive,
            hover: state.hover, active: state.active,
            showFocusRing: false, direction, clip, flush, extra: style,
        }), ...(interactive ? bind : {}), children: children }));
}
function ClickableCard({ pad = 'default', tone, accentBorder, bordered = true, onClick, direction, clip, children, style, className, id, }) {
    const p = resolvePad(pad);
    const flush = pad === 'flush';
    const { state, bind } = useInteractive({ onClick });
    const { isFocusVisible, focusProps } = useFocusRing();
    return (_jsx("div", { id: id, className: className, tabIndex: 0, role: "button", style: baseStyle({
            p, tone, accentBorder, bordered, interactive: true,
            hover: state.hover, active: state.active,
            showFocusRing: isFocusVisible, direction, clip, flush, extra: style,
        }), ...bind, ...focusProps, children: children }));
}
//# sourceMappingURL=Card.js.map